import React, { useState, useEffect, useRef } from "react";
import { useGetChatMessagesQuery } from "../redux/apislice"; 
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";

const ChatScreen = ({
  userType = "buyer",
  chatWith,
  conversationId: propConversationId,
  isMobile,
  onBack,
}) => {
  const conversationId = propConversationId ?? chatWith ?? 1;

  const [page, setPage] = useState(1);
  const limit = 20;

  const {
    data: apiResponse,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useGetChatMessagesQuery(
    { conversationId, page, limit },
    { refetchOnMountOrArgChange: true }
  );

  const [messages, setMessages] = useState([]);

  const [totalPages, setTotalPages] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [input, setInput] = useState("");
  const [media, setMedia] = useState(null);
  const [mediaPreviewKey, setMediaPreviewKey] = useState(Date.now());
  const [mediaData, setMediaData] = useState(null);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [menuVisible, setMenuVisible] = useState(false);
  const [dropdownOpenId, setDropdownOpenId] = useState(null);

  const scrollableRef = useRef(null);
  const bottomRef = useRef(null);

  const scrollToBottom = (smooth = true) => {
    bottomRef.current?.scrollIntoView({
      behavior: smooth ? "smooth" : "auto",
    });
    setHasUnread(false);
  };

  useEffect(() => {
    if (apiResponse && Array.isArray(apiResponse.data)) {
      const mapped = apiResponse.data.map((msg) => ({
        id: msg.id,
        sender: msg.senderType ? msg.senderType.toLowerCase() : "user", 
        text: msg.text ?? "",
        time: msg.createdAt
          ? new Date(msg.createdAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
        createdAt: msg.createdAt,
        user: msg.user ?? null, 
        seller: msg.seller ?? null,
        isRead: !!msg.isRead,
        media: null,
        type: "text",
      }));

      setMessages((prev) => (page === 1 ? mapped : [...mapped, ...prev]));

      setTotalPages(apiResponse.totalPages ?? 1);
      setCurrentPage(apiResponse.currentPage ?? page);
      setHasNextPage(!!apiResponse.hasNextPage);
    }
  }, [apiResponse, page]);

  useEffect(() => {
    scrollToBottom(false);
  }, []);

  useEffect(() => {
    scrollToBottom(true);
  }, [messages]);

  const stopRecordingAndSend = () => {
    return new Promise((resolve) => {
      if (!recorder) return resolve();

      recorder.onstop = () => {
        const audio = recorder.stream?.getAudioTracks?.()[0];
        if (audio) audio.stop();

        const blob = new Blob(recorder.chunks || [], { type: "audio/webm" });
        const audioUrl = URL.createObjectURL(blob);

        const newMsg = {
          id: Date.now(),
          sender: userType,
          text: `🎤 Voice Message`,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          media: audioUrl,
          type: "voice",
          pinned: false,
          createdAt: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, newMsg]);
        setAudioBlob(null);
        setIsRecording(false);

        resolve();
      };

      try {
        recorder.stop();
      } catch (e) {
        resolve();
      }
    });
  };

  const handleSend = async () => {
    if (isRecording && recorder) {
      await stopRecordingAndSend();
      return;
    }

    if (!input.trim() && !media && !audioBlob) return;

    if (audioBlob) {
      const audioUrl = URL.createObjectURL(audioBlob);
      const newMsg = {
        id: Date.now(),
        sender: userType,
        text: `🎤 Voice Message`,
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        media: audioUrl,
        type: "voice",
        pinned: false,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, newMsg]);
      setAudioBlob(null);
      refetch(); 
      return;
    }

    let messageType = "text";
    const fileType = media?.type;
    if (fileType) {
      if (fileType.includes("image")) messageType = "image";
      else if (fileType.includes("pdf")) messageType = "pdf";
      else if (fileType.includes("word")) messageType = "doc";
      else if (fileType.includes("excel")) messageType = "excel";
      else messageType = "file";
    }

    const newMsg = {
      id: Date.now(),
      sender: userType,
      text: input,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      media: mediaData || (media ? URL.createObjectURL(media) : null),
      type: media ? messageType : "text",
      pinned: false,
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, newMsg]);
    setInput("");
    setMedia(null);
    setMediaData(null);
    setMediaPreviewKey(Date.now());
    refetch();
  };

  const scrollToSearchedMessage = (callback) => {
    if (!scrollableRef.current) return;
    const container = scrollableRef.current;
    const found = container.querySelector(".searched-msg");

    if (found) {
      found.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof callback === "function") {
        setTimeout(() => callback(), 500);
      }
    } else if (typeof callback === "function") {
      callback();
    }
  };

  const handleLoadMore = () => {
    if (hasNextPage) {
      setPage((p) => p + 1);
    }
  };

  if (isLoading && page === 1) {
    return <div className="flex items-center justify-center h-full">Loading chat...</div>;
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full text-red-500">
        Failed to load chat. Try refreshing.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen w-full bg-white relative max-w-full sm:max-w-full md:max-w-screen-md lg:max-w-screen-lg mx-auto overflow-hidden">
      <ChatHeader
        chatWith={chatWith}
        menuVisible={menuVisible}
        setMenuVisible={setMenuVisible}
        showSearch={showSearch}
        setShowSearch={setShowSearch}
        search={search}
        setSearch={setSearch}
        clearAllMessages={() => setMessages([])}
        scrollToSearchedMessage={scrollToSearchedMessage}
        isMobile={isMobile}
        onBack={onBack}
      />

      <div className="flex-1 overflow-y-auto">
        {isFetching && page > 1 && (
          <div className="text-center text-sm text-gray-500 py-2">Loading more messages...</div>
        )}

        {hasNextPage && (
          <div className="w-full flex justify-center mt-2">
            <button
              onClick={handleLoadMore}
              className="px-3 py-1 text-sm bg-gray-100 border rounded-md hover:bg-gray-200"
            >
              Load older messages
            </button>
          </div>
        )}

        <MessageList
          messages={messages}
          userType={userType}
          search={search}
          showSearch={showSearch}
          editingId={editingId}
          setEditingId={setEditingId}
          editedText={editedText}
          setEditedText={setEditedText}
          updateMessages={setMessages}
          scrollableRef={scrollableRef}
          bottomRef={bottomRef}
          setHasUnread={setHasUnread}
          dropdownOpenId={dropdownOpenId}
          setDropdownOpenId={setDropdownOpenId}
        />
      </div>

      {isRecording && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-2 py-1 rounded-full shadow-lg animate-pulse text-[10px] sm:text-xs md:text-sm w-[90vw] max-w-[90vw] text-center z-50">
          🎙️ Recording Voice Message...
        </div>
      )}

      {media && (
        <div key={mediaPreviewKey} className="flex items-center gap-4 p-3 bg-gray-100 border-t flex-wrap">
          <div className="flex-1 text-sm overflow-hidden text-ellipsis whitespace-nowrap">
            📎 <span className="font-medium break-all">{media.name}</span>
          </div>
          <button
            className="text-red-500 text-xs hover:underline"
            onClick={() => {
              setMedia(null);
              setMediaData(null);
              setMediaPreviewKey(Date.now());
            }}
          >
            Remove
          </button>
        </div>
      )}

      {hasUnread && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-24 right-4 bg-yellow-400 text-xs sm:text-sm px-3 sm:px-4 py-1 rounded-full shadow-md"
        >
          🔔 New Messages
        </button>
      )}

      <div className="sticky bottom-0 bg-white">
        <ChatInput
          input={input}
          setInput={setInput}
          media={media}
          setMedia={setMedia}
          isRecording={isRecording}
          setIsRecording={setIsRecording}
          recorder={recorder}
          setRecorder={setRecorder}
          audioBlob={audioBlob}
          setAudioBlob={setAudioBlob}
          handleSend={handleSend}
          handleKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          setMediaData={setMediaData}
        />
      </div>
    </div>
  );
};

export default ChatScreen;